import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import { Bell, Zap, DollarSign, X, Settings, Check, ChevronRight, Briefcase, MapPin, Filter } from 'lucide-react';

const INDUSTRIES = ['Healthcare', 'Hospitality', 'Industrial', 'Retail', 'Technology', 'Construction', 'Education', 'Security'];
const CATEGORIES = ['On-site', 'Remote', 'Hybrid'];
const SKILLS = ['Nursing', 'CPR', 'First Aid', 'CNA', 'Medication Administration', 'Patient Care', 'Forklift', 'Cleaning', 'Cooking', 'Customer Service', 'Security', 'Driving', 'Childcare', 'Elder Care', 'Vital Signs'];

export default function JobAlerts({ asDropdown = false, onClose, onApplyFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    enabled: true, categories: [], industries: [], skills: [], min_pay_rate: 0, notify_surge: true
  });

  useEffect(() => { loadAlerts(); }, []);

  const loadAlerts = async () => {
    try {
      await apiCall('/api/worker/job-alerts/seed', { method: 'POST' }).catch(() => {});
      const res = await apiCall('/api/worker/job-alerts');
      setData(res);
      setPrefs(res.preferences || prefs);
    } catch (e) {
      console.error('Failed to load alerts');
    } finally { setLoading(false); }
  };

  const updatePrefs = async () => {
    try {
      await apiCall('/api/worker/job-alerts/preferences', { method: 'PUT', body: JSON.stringify(prefs) });
      toast.success('Alert preferences updated');
      setShowPrefs(false);
    } catch { toast.error('Failed to update preferences'); }
  };

  const markAllRead = async () => {
    try {
      await apiCall('/api/worker/job-alerts/mark-read', { method: 'POST' });
      loadAlerts();
    } catch {}
  };

  const toggleArray = (arr, val) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  // Dispatch a filter to the parent (WorkerDashboard) which switches to the
  // Find Jobs tab and applies it. Closes the dropdown afterwards.
  const applyFilterToFindJobs = (filter, label) => {
    if (onApplyFilter) {
      onApplyFilter(filter);
      if (label) toast.success(`Filtered by ${label}`);
      if (onClose) onClose();
    }
  };

  // Map an active-filter chip (string) back to a structured filter object
  const filterFromChip = (chip) => {
    if (!chip) return null;
    if (/^\$\d+\+\/hr$/.test(chip)) {
      return { pay_min: parseInt(chip.match(/\d+/)[0], 10) };
    }
    if (CATEGORIES.includes(chip)) return { category: chip };
    if (INDUSTRIES.includes(chip)) return { industry: chip };
    if (SKILLS.includes(chip)) return { search: chip };
    return { search: chip };
  };

  const filterFromAlert = (alert) => {
    const f = {};
    if (alert.industry) f.industry = alert.industry;
    if (alert.category) f.category = alert.category;
    if (alert.pay_rate) f.pay_min = Math.max(0, parseInt(alert.pay_rate, 10) - 1);
    return f;
  };

  if (loading) return asDropdown ? (
    <div className="p-4 text-center"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent mx-auto" /></div>
  ) : null;

  if (!data) return null;

  const activeFilters = [
    ...(prefs.categories || []),
    ...(prefs.industries || []),
    ...(prefs.skills || []).slice(0, 3),
    ...(prefs.min_pay_rate > 0 ? [`$${prefs.min_pay_rate}+/hr`] : [])
  ];

  if (asDropdown) {
    return (
      <div className="w-96 max-h-[28rem] overflow-y-auto" data-testid="job-alerts-dropdown">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-600" /> Job Alerts
            {data.unread_count > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full font-bold">{data.unread_count}</span>}
          </h3>
          <div className="flex items-center gap-1">
            {data.unread_count > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-blue-600 hover:underline mr-1" data-testid="mark-all-read-btn">Mark all read</button>
            )}
            <button onClick={() => setShowPrefs(!showPrefs)} className="p-1.5 hover:bg-gray-100 rounded-lg transition" data-testid="alert-settings-btn">
              <Settings className="w-4 h-4 text-gray-500" />
            </button>
            {onClose && <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition"><X className="w-4 h-4 text-gray-500" /></button>}
          </div>
        </div>

        {/* Active Filter Summary */}
        {activeFilters.length > 0 && !showPrefs && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-1.5 flex-wrap" data-testid="active-filter-summary">
            <Filter className="w-3 h-3 text-blue-500 flex-shrink-0" />
            {activeFilters.slice(0, 5).map((f, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">{f}</span>
            ))}
            {activeFilters.length > 5 && <span className="text-[10px] text-blue-500">+{activeFilters.length - 5} more</span>}
          </div>
        )}

        {showPrefs ? (
          <div className="p-4 space-y-4" data-testid="alert-preferences-panel">
            {/* Categories */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1.5">Job Category</p>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map(c => (
                  <button key={c} onClick={() => setPrefs(p => ({...p, categories: toggleArray(p.categories || [], c)}))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${(prefs.categories || []).includes(c) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
                    data-testid={`pref-category-${c.toLowerCase()}`}
                  >{c}</button>
                ))}
              </div>
            </div>

            {/* Industries */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1.5">Industries</p>
              <div className="flex flex-wrap gap-1.5">
                {INDUSTRIES.map(ind => (
                  <button key={ind} onClick={() => setPrefs(p => ({...p, industries: toggleArray(p.industries || [], ind)}))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${(prefs.industries || []).includes(ind) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}
                    data-testid={`pref-industry-${ind.toLowerCase()}`}
                  >{ind}</button>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1.5">Required Skills</p>
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                {SKILLS.map(sk => (
                  <button key={sk} onClick={() => setPrefs(p => ({...p, skills: toggleArray(p.skills || [], sk)}))}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${(prefs.skills || []).includes(sk) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300'}`}
                    data-testid={`pref-skill-${sk.toLowerCase().replace(/\s/g, '-')}`}
                  >{sk}</button>
                ))}
              </div>
            </div>

            {/* Min Pay Rate */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1.5">Minimum Pay Rate ($/hr)</p>
              <input type="number" min="0" step="5" value={prefs.min_pay_rate || 0}
                onChange={e => setPrefs(p => ({...p, min_pay_rate: parseInt(e.target.value) || 0}))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="pref-min-pay"
              />
            </div>

            {/* Surge toggle */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">Surge Alerts</span>
              <button onClick={() => setPrefs(p => ({...p, notify_surge: !p.notify_surge}))}
                className={`w-10 h-5 rounded-full transition ${prefs.notify_surge ? 'bg-blue-600' : 'bg-gray-300'}`}
                data-testid="pref-surge-toggle"
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${prefs.notify_surge ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <button onClick={updatePrefs} className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition" data-testid="save-alert-prefs-btn">
              Save Preferences
            </button>
          </div>
        ) : (
          <>
            {data.recent_alerts.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="font-medium">No job alerts yet</p>
                <p className="text-xs text-gray-400 mt-1">Set your preferences to receive matching alerts</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {data.recent_alerts.map((alert, i) => (
                  <button
                    key={i}
                    onClick={() => applyFilterToFindJobs(filterFromAlert(alert), alert.job_title)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition cursor-pointer ${!alert.read ? 'bg-blue-50/50' : ''}`}
                    data-testid={`job-alert-item-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-lg mt-0.5 ${alert.is_surge ? 'bg-red-100' : 'bg-blue-100'}`}>
                        {alert.is_surge ? <Zap className="w-3.5 h-3.5 text-red-600" /> : <Bell className="w-3.5 h-3.5 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!alert.read ? 'font-bold' : 'font-medium'} text-gray-900 truncate`}>{alert.job_title}</p>
                        <p className="text-xs text-gray-500">{alert.employer}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-emerald-600 font-medium">${alert.pay_rate}/hr</span>
                          {alert.industry && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{alert.industry}</span>}
                          {alert.category && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{alert.category}</span>}
                          {alert.is_surge && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">SURGE</span>}
                        </div>
                      </div>
                      {!alert.read && <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" />}
                      <ChevronRight className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Full page mode
  return (
    <div className="space-y-6" data-testid="job-alerts-page">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/15 rounded-xl backdrop-blur-sm">
            <Bell className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Job Alerts</h2>
            <p className="text-white/70 text-sm">{data.unread_count} unread alert{data.unread_count !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      {/* Active Filter Summary */}
      {activeFilters.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="active-filter-summary-page">
          <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1"><Filter className="w-3.5 h-3.5" /> Active Filters {onApplyFilter && <span className="text-blue-400">— click to filter Find Jobs</span>}</p>
          <div className="flex flex-wrap gap-1.5">
            {activeFilters.map((f, i) => (
              <button
                key={i}
                onClick={() => applyFilterToFindJobs(filterFromChip(f), f)}
                className="px-2 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-xs font-medium transition cursor-pointer"
                data-testid={`active-filter-chip-page-${i}`}
              >{f}</button>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {data.recent_alerts.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>No alerts yet. Configure your preferences to start receiving job alerts.</p>
          </div>
        ) : data.recent_alerts.map((alert, i) => (
          <button
            key={i}
            onClick={() => applyFilterToFindJobs(filterFromAlert(alert), alert.job_title)}
            className={`w-full text-left flex items-center justify-between px-6 py-4 border-b border-gray-50 last:border-0 hover:bg-blue-50 transition ${!alert.read ? 'bg-blue-50/30' : ''}`}
            data-testid={`job-alert-page-${i}`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-2.5 rounded-xl ${alert.is_surge ? 'bg-red-100' : 'bg-blue-100'}`}>
                {alert.is_surge ? <Zap className="w-5 h-5 text-red-600" /> : <Bell className="w-5 h-5 text-blue-600" />}
              </div>
              <div>
                <p className="font-bold text-gray-900">{alert.job_title}</p>
                <p className="text-sm text-gray-500">{alert.employer}</p>
                <div className="flex gap-3 mt-1">
                  <span className="text-sm text-emerald-600 font-semibold">${alert.pay_rate}/hr</span>
                  {alert.industry && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{alert.industry}</span>}
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        ))}
      </div>
    </div>
  );
}
